import React from "react";
import { Link } from "react-router-dom";
import { Facebook, Linkedin } from "lucide-react";
import { COMPANY, NAV_LINKS, SERVICES, CLIENT_SITES } from "../../mock";

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer className="bg-slate-950 text-slate-300 pt-20 pb-10 relative overflow-hidden">
      <div
        className="absolute inset-x-0 top-0 h-px"
        style={{
          background:
            "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
        }}
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="flex items-center gap-2">
              <div className="h-10 w-10 rounded-xl bg-sky-500 text-white flex items-center justify-center font-bold text-sm">
                AW
              </div>
              <div>
                <div className="font-semibold text-white">{COMPANY.name}</div>
                <div className="text-xs text-slate-400">
                  Social Media Consultancy & Technical Solutions
                </div>
              </div>
            </div>
            <p className="mt-5 text-sm text-slate-400 max-w-sm leading-relaxed">
              Modern websites, custom software and AI-powered tools — built
              by a small senior team in Lanarkshire, Scotland.
            </p>
            <div className="mt-6 flex gap-2">
              <a
                href="https://www.facebook.com/profile.php?id=61575813896122"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow Andy Watson IT on Facebook"
                className="h-10 w-10 rounded-full border border-white/15 hover:border-white hover:bg-white hover:text-slate-900 transition-colors flex items-center justify-center"
              >
                <Facebook className="h-4 w-4" />
              </a>
              <a
                href="https://www.facebook.com/profile.php?id=61585879055443"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow Andy Watson IT on Facebook (second page)"
                className="h-10 w-10 rounded-full border border-white/15 hover:border-white hover:bg-white hover:text-slate-900 transition-colors flex items-center justify-center"
              >
                <Facebook className="h-4 w-4" />
              </a>
              <a
                href="https://share.google/hlrhIPzcpzNGDWdWb"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Connect with Andy Watson on LinkedIn"
                className="h-10 w-10 rounded-full border border-white/15 hover:border-white hover:bg-white hover:text-slate-900 transition-colors flex items-center justify-center"
              >
                <Linkedin className="h-4 w-4" />
              </a>
            </div>
          </div>

          <div className="md:col-span-2">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-4">
              Navigate
            </div>
            <ul className="space-y-2.5 text-sm">
              {NAV_LINKS.map((l) => (
                <li key={l.label}>
                  <a
                    href={l.href}
                    className="text-slate-300 hover:text-white transition-colors"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-4">
              Services
            </div>
            <ul className="space-y-2.5 text-sm">
              {SERVICES.map((s) => (
                <li key={s.id}>
                  <a
                    href="#services"
                    className="text-slate-300 hover:text-white transition-colors"
                  >
                    {s.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-4">
              Get in touch
            </div>
            <ul className="space-y-2.5 text-sm">
              <li>
                <a
                  href={`mailto:${COMPANY.email}`}
                  className="hover:text-white transition-colors break-all"
                >
                  {COMPANY.email}
                </a>
              </li>
              <li>
                <a
                  href={`tel:${COMPANY.phoneRaw}`}
                  className="hover:text-white transition-colors"
                >
                  {COMPANY.phone}
                </a>
              </li>
              <li className="text-slate-400">{COMPANY.address}</li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-white/10" data-testid="footer-backlinks">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-4">
            Sites we&rsquo;ve built &amp; support
          </div>
          <ul className="flex flex-wrap gap-x-6 gap-y-3 text-sm">
            {CLIENT_SITES.map((s) => (
              <li key={s.url}>
                <a
                  href={s.url}
                  target="_blank"
                  rel="noopener"
                  data-testid={`backlink-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="group flex items-center gap-2.5 text-slate-300 hover:text-sky-400 transition-colors"
                >
                  <span className={`h-9 min-w-9 max-w-[88px] px-1 rounded-lg ${s.darkTile ? "bg-slate-800" : "bg-white"} flex items-center justify-center overflow-hidden ring-1 ring-white/10 group-hover:ring-sky-400 transition-shadow`}>
                    <img
                      src={s.logo}
                      alt={`${s.name} logo`}
                      loading="lazy"
                      className="h-7 w-auto max-w-full object-contain"
                    />
                  </span>
                  {s.name}
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-10 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
          <div>
            © {year} {COMPANY.name}. All rights reserved.
          </div>
          <div className="flex gap-5">
            <Link to="/terms" className="hover:text-white">Terms & Conditions</Link>
            <Link to="/web-design-terms" data-testid="footer-web-design-terms-link" className="hover:text-white">Web Design Terms</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
